<template>
   <!-- Page loading or show slot if finished loading -->
   <div class="relative w-full">
      <transition name="portal-fade" mode="out-in" @after-enter="onTransitionEnd">
         <!-- Loading -->
         <div v-if="isLoading" data-cy="page-loader-loading" class="flex min-h-screen items-center justify-center">
            <LoadingSpinner size="lg" />
         </div>

         <!-- Error section -->
         <div
            v-else-if="hasLoadErrors"
            class="flex min-h-screen w-full flex-col items-center justify-center bg-brand-light text-brand-dark"
         >
            <div class="text-regular max-w-md text-center" data-cy="pageloader-error-message">
               <!-- Heading -->
               <h1 class="px-4 text-3xl md:text-4xl">{{ $t("validation.error_generic_msg") }}</h1>

               <h2 data-cy="page-loader-subheading" class="mt-6 px-4 text-xl md:text-2xl">
                  {{ loadErrorMessage || $t("system.errors.page_loading.unexpected_error") }}
               </h2>

               <div class="mt-6 text-left">
                  <p>{{ $t("system.errors.page_loading.options_to_help") }}</p>
                  <ul class="mt-2">
                     <li>{{ $t("system.errors.page_loading.option_go_home") }}</li>
                     <li class="mt-2">{{ $t("system.errors.page_loading.option_notify_support") }}</li>
                  </ul>
                  <p class="mt-6">{{ $t("system.errors.page_loading.hope_to_help") }}</p>
               </div>

               <!-- User actions -->
               <div class="mt-6 flex grow flex-wrap items-center justify-center gap-3">
                  <!-- Go Home -->
                  <LinkButton :url="authStore.authTokenExists ? '/home' : '/'" class="mr-4" size="sm" data-cy="to-home">
                     {{ $t("redirects.go_to_home") }}
                  </LinkButton>

                  <!-- let us know (intercom or email) -->
                  <SupportButton size="sm" :text="$t('system.notify_support')" />
               </div>
            </div>
         </div>

         <!-- Component slot -->
         <div v-else>
            <slot />
         </div>
      </transition>
   </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/authStore";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useRoute } from "vue-router";

const $emit = defineEmits(["transition-end"]);

const authStore = useAuthStore();
const { t: $t } = useI18n();
const $route = useRoute();

interface Props {
   isLoading?: boolean;
   loadErrors?: Error[];
   loadErrorMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
   isLoading: true,
   loadErrors: null,
});

const hasLoadErrors = computed(() => {
   const hasErrors = props.loadErrors?.length > 0;
   if (hasErrors) {
      // log to sentry
      console.error("Page loading error: ", props.loadErrors);
   }

   return hasErrors;
});

const onTransitionEnd = () => {
   // Its important to note that this is JUST the transition end, not that the page loaded successfully (its up to you to handle that in the parent component)
   // We are using this for example on the account where we need to scroll to an area on successful load
   $emit("transition-end");
};
</script>

<style scoped lang="scss"></style>
